<script setup lang="ts">
import { ref, reactive } from "vue";
import {
  Easing,
  EasingFunction,
  SupportedEaseNameToCubicBezier,
  Timeline,
  TweenValue
} from "@zyzgroup/core-web";
import Box from "./Box.vue";
import CSSAnimation, { SupportedCSSAnimation } from "./CSSAnimation.vue";

export type SupportedPageEffect =
  | "static"
  | "fade"
  | "moveFromLeft"
  | "moveToLeft"
  | "moveFromRight"
  | "moveToRight"
  | "moveFromTop"
  | "moveToTop"
  | "moveFromBottom"
  | "moveToBottom"
  | "moveFromLeftFade"
  | "moveToLeftFade"
  | "moveFromRightFade"
  | "moveToRightFade"
  | "moveFromTopFade"
  | "moveToTopFade"
  | "moveFromBottomFade"
  | "moveToBottomFade"
  | "scaleFromSmall"
  | "scaleToSmall"
  | "scaleFromLarge"
  | "scaleToLarge"
  | "scaleFromSmallCenter"
  | "scaleToSmallCenter"
  | "rotateRightSideFirst"
  | "rotateLeftSideFirst"
  | "rotateTopSideFirst"
  | "rotateBottomSideFirst"
  | "flipOutRight"
  | "flipInLeft"
  | "flipOutLeft"
  | "flipInRight"
  | "flipOutTop"
  | "flipInBottom"
  | "flipOutBottom"
  | "flipInTop"
  | "rotateFall"
  | "rotateOutNewspaper"
  | "rotateInNewspaper"
  | "rotatePushLeft"
  | "rotatePushRight"
  | "rotatePushTop"
  | "rotatePushBottom"
  | "rotatePullRight"
  | "rotatePullLeft"
  | "rotatePullTop"
  | "rotatePullBottom"
  | "rotateFoldRight"
  | "rotateFoldLeft"
  | "rotateFoldTop"
  | "rotateFoldBottom"
  | "rotateUnfoldLeft"
  | "rotateUnfoldRight"
  | "rotateUnfoldTop"
  | "rotateUnfoldBottom"
  | "rotateRoomLeftOut"
  | "rotateRoomLeftIn"
  | "rotateRoomRightOut"
  | "rotateRoomRightIn"
  | "rotateRoomTopOut"
  | "rotateRoomTopIn"
  | "rotateRoomBottomOut"
  | "rotateRoomBottomIn"
  | "rotateCubeLeftOut"
  | "rotateCubeLeftIn"
  | "rotateCubeRightOut"
  | "rotateCubeRightIn"
  | "rotateCubeTopOut"
  | "rotateCubeTopIn"
  | "rotateCubeBottomOut"
  | "rotateCubeBottomIn"
  | "rotateCarouselLeftOut"
  | "rotateCarouselLeftIn"
  | "rotateCarouselRightOut"
  | "rotateCarouselRightIn"
  | "rotateCarouselTopOut"
  | "rotateCarouselTopIn"
  | "rotateCarouselBottomOut"
  | "rotateCarouselBottomIn"
  | "rotateSidesOut"
  | "rotateSidesIn"
  | "rotateSlideOut"
  | "rotateSlideIn";

// type AtomEffect =
//   | "moveFromLeft"
//   | "moveToLeft"
//   | "moveFromRight"
//   | "moveToRight"
//   | "moveFromTop"
//   | "moveToTop"
//   | "moveFromBottom"
//   | "moveToBottom"
//   | "fadeOut"
//   | "fadeIn"
//   | "disappear"
//   | "appear"
//   | "scaleFromSmall"
//   | "scaleToSmall"
//   | "scaleFromLarge"
//   | "scaleToLarge"
//   | "scaleFromSmallCenter"
//   | "scaleToSmallCenter";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { CSSAnimation },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    effect?: SupportedPageEffect;
    duration?: number;
    /// js ease
    easeFunction?: EasingFunction;
    /// css ease
    timingFunction?: SupportedEaseNameToCubicBezier;
    delay?: number;
  }>(),
  {
    effect: "moveFromLeft",
    duration: 500,
    easeFunction: Easing.Cubic.InOut,
    timingFunction: "ease",
    delay: 0
  }
);

const emits = defineEmits<{
  (
    event: "mount",
    open: (effect?: SupportedPageEffect) => void,
    close: (effect?: SupportedPageEffect) => void
  ): void;
  (event: "pageOpenStart"): void;
  (event: "pageOpenEnd"): void;
  (event: "pageCloseStart"): void;
  (event: "pageCloseEnd"): void;
}>();

const pageRef = ref();
const style = reactive<Record<string, string>>({});

const cssAnimationRef = ref();
let cssAnimationName: SupportedCSSAnimation = "rotateRightSideFirst";

function runDependOnEffectType(
  cssAnimationTask: () => void,
  jsAnimationTask: () => void
) {
  switch (props.effect) {
    case "static":
      // JS Animation
      jsAnimationTask();
      break;
    default:
      // CSS Animation
      cssAnimationTask();
      break;
  }
}

// const _addAtomEffect = (
//   attrFrom: Record<string, number>,
//   attrTo: Record<string, number>,
//   effect: AtomEffect
// ) => {
//   switch (effect) {
//     case "moveFromLeft":
//       attrFrom.x = -100;
//       attrTo.x = 0;
//       break;
//     case "moveToLeft":
//       attrFrom.x = 0;
//       attrTo.x = -100;
//       break;
//     case "moveFromRight":
//       attrFrom.x = 100;
//       attrTo.x = 0;
//       break;
//     case "moveToRight":
//       attrFrom.x = 0;
//       attrTo.x = 100;
//       break;
//     case "moveFromTop":
//       attrFrom.y = -100;
//       attrTo.y = 0;
//       break;
//     case "moveToTop":
//       attrFrom.y = 0;
//       attrTo.y = -100;
//       break;
//     case "moveFromBottom":
//       attrFrom.y = 100;
//       attrTo.y = 0;
//       break;
//     case "moveToBottom":
//       attrFrom.y = 0;
//       attrTo.y = 100;
//       break;
//     case "fadeOut":
//       attrFrom.opacity = 1;
//       attrTo.opacity = 0.3;
//       break;
//     case "fadeIn":
//       attrFrom.opacity = 0.3;
//       attrTo.opacity = 1;
//       break;
//     case "disappear":
//       attrFrom.opacity = 1;
//       attrTo.opacity = 0;
//       break;
//     case "appear":
//       attrFrom.opacity = 0;
//       attrTo.opacity = 1;
//       break;
//     case "scaleFromSmall":
//       attrFrom.scale = 0.8;
//       attrTo.scale = 1;
//       break;
//     case "scaleToSmall":
//       attrFrom.scale = 1;
//       attrTo.scale = 0.8;
//       break;
//     case "scaleFromLarge":
//       attrFrom.scale = 1.2;
//       attrTo.scale = 1;
//       break;
//     case "scaleToLarge":
//       attrFrom.scale = 1;
//       attrTo.scale = 1.2;
//       break;
//     case "scaleFromSmallCenter":
//       attrFrom.scale = 0.7;
//       attrTo.scale = 1;
//       break;
//     case "scaleToSmallCenter":
//       attrFrom.scale = 1;
//       attrTo.scale = 0.7;
//       break;
//   }
// };

let isOpen = true;

const timeline = new Timeline();
const changeEffect = (effect: SupportedPageEffect) => {
  const triggerFrom: Record<string, number> = {};
  const triggerTo: Record<string, number> = {};
  switch (effect) {
    case "static":
      break;
    // 统一用 css animation ，抹平与 js animation 的差异
    // case "moveFromLeft":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveFromLeft");
    //   break;
    // case "moveToLeft":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveToLeft");
    //   break;
    // case "moveFromRight":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveFromRight");
    //   break;
    // case "moveToRight":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveToRight");
    //   break;
    // case "moveFromTop":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveFromTop");
    //   break;
    // case "moveToTop":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveToTop");
    //   break;
    // case "moveFromBottom":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveFromBottom");
    //   break;
    // case "moveToBottom":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveToBottom");
    //   break;
    // case "fade":
    //   _addAtomEffect(triggerFrom, triggerTo, "fadeOut");
    //   break;
    // case "moveFromLeftFade":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveFromLeft");
    //   _addAtomEffect(triggerFrom, triggerTo, "fadeIn");
    //   break;
    // case "moveToLeftFade":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveToLeft");
    //   _addAtomEffect(triggerFrom, triggerTo, "fadeOut");
    //   break;
    // case "moveFromRightFade":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveFromRight");
    //   _addAtomEffect(triggerFrom, triggerTo, "fadeIn");
    //   break;
    // case "moveToRightFade":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveToRight");
    //   _addAtomEffect(triggerFrom, triggerTo, "fadeOut");
    //   break;
    // case "moveFromTopFade":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveFromTop");
    //   _addAtomEffect(triggerFrom, triggerTo, "fadeIn");
    //   break;
    // case "moveToTopFade":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveToTop");
    //   _addAtomEffect(triggerFrom, triggerTo, "fadeOut");
    //   break;
    // case "moveFromBottomFade":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveFromBottom");
    //   _addAtomEffect(triggerFrom, triggerTo, "fadeIn");
    //   break;
    // case "moveToBottomFade":
    //   _addAtomEffect(triggerFrom, triggerTo, "moveToBottom");
    //   _addAtomEffect(triggerFrom, triggerTo, "fadeOut");
    //   break;
    // case "scaleFromSmall":
    //   _addAtomEffect(triggerFrom, triggerTo, "scaleFromSmall");
    //   _addAtomEffect(triggerFrom, triggerTo, "appear");
    //   break;
    // case "scaleToSmall":
    //   _addAtomEffect(triggerFrom, triggerTo, "scaleToSmall");
    //   _addAtomEffect(triggerFrom, triggerTo, "disappear");
    //   break;
    // case "scaleFromLarge":
    //   _addAtomEffect(triggerFrom, triggerTo, "scaleFromLarge");
    //   _addAtomEffect(triggerFrom, triggerTo, "appear");
    //   break;
    // case "scaleToLarge":
    //   _addAtomEffect(triggerFrom, triggerTo, "scaleToLarge");
    //   _addAtomEffect(triggerFrom, triggerTo, "disappear");
    //   break;
    // case "scaleFromSmallCenter":
    //   _addAtomEffect(triggerFrom, triggerTo, "scaleFromSmallCenter");
    //   _addAtomEffect(triggerFrom, triggerTo, "appear");
    //   break;
    // case "scaleToSmallCenter":
    //   _addAtomEffect(triggerFrom, triggerTo, "scaleToSmallCenter");
    //   _addAtomEffect(triggerFrom, triggerTo, "disappear");
    //   break;
  }
  timeline.clear();
  timeline.frameDuration = props.duration;
  runDependOnEffectType(
    () => {
      // CSS Animation
      cssAnimationName = effect as SupportedCSSAnimation;
    },
    () => {
      // JS Animation
      timeline.addTween(triggerFrom, triggerTo, {
        delay: props.delay / props.duration,
        easeFunction: props.easeFunction,
        onStart: () => {
          if (isOpen) {
            emits("pageOpenStart");
          } else {
            emits("pageCloseStart");
          }
        },
        onUpdate: (attrs: TweenValue) => {
          style.transform = "";
          if ("x" in attrs) {
            style.transform += ` translateX(${attrs.x}%)`;
          }
          if ("y" in attrs) {
            style.transform += ` translateY(${attrs.y}%)`;
          }
          if ("rx" in attrs) {
            style.transform += ` rotateX(${attrs.rx}deg)`;
          }
          if ("ry" in attrs) {
            style.transform += ` rotateY(${attrs.ry}deg)`;
          }
          if ("scale" in attrs) {
            style.transform += ` scale(${attrs.scale})`;
          }
          if ("opacity" in attrs) {
            style.opacity = `${attrs.opacity}`;
          }
        },
        onComplete: () => {
          if (isOpen) {
            emits("pageOpenEnd");
          } else {
            emits("pageCloseEnd");
          }
        }
      });
    }
  );
};

const cssanimationstart = () => {
  if (isOpen) {
    emits("pageOpenStart");
  } else {
    emits("pageCloseStart");
  }
};

const cssanimationend = () => {
  if (isOpen) {
    emits("pageOpenEnd");
  } else {
    emits("pageCloseEnd");
  }
};

// 清除
const clear = () => {
  // clear js style
  style.transform = "none";
  style.opacity = "1";
  // clear css style
  cssAnimationRef.value?.clear();
};

const open = (newEffect?: SupportedPageEffect) => {
  changeEffect(newEffect || props.effect);
  isOpen = true;
  runDependOnEffectType(
    () => {
      // CSS Animation
      // clear js style
      style.transform = "none";
      style.opacity = "1";
      cssAnimationRef.value?.trigger(cssAnimationName);
    },
    () => {
      // JS Animation
      // clear css style
      cssAnimationRef.value?.clear();
      timeline.play();
    }
  );
};

const close = (newEffect?: SupportedPageEffect) => {
  changeEffect(newEffect || props.effect);
  isOpen = false;
  runDependOnEffectType(
    () => {
      // CSS Animation
      cssAnimationRef.value?.triggerReverse(cssAnimationName);
    },
    () => {
      // JS Animation
      timeline.reverse();
    }
  );
};

defineExpose({
  open,
  close,
  clear
});
</script>

<template>
  <CSSAnimation
    ref="cssAnimationRef"
    class="page-inner"
    :style="{
      position: 'relative',
      display: 'block',
      width: '100%',
      height: '100%',
      overflow: 'scroll',
      backfaceVisibility: 'hidden',
      transformStyle: 'preserve-3d',
      ...style
    }"
    :duration="props.duration"
    :delay="props.delay"
    :timingFunction="props.timingFunction"
    @cssanimationstart="cssanimationstart"
    @cssanimationend="cssanimationend"
  >
    <Box
      ref="pageRef"
      class="page"
      display-type="full"
      :style="{
        overflow: 'hidden'
      }"
      @mount="() => emits('mount', open, close)"
    >
      <slot></slot>
    </Box>
  </CSSAnimation>
</template>
